/**
 * MobileLayout — routes between ChoiceScreen, OfficerTaskView, and MobileCommandView.
 *
 * Default landing is the ChoiceScreen menu (matches FOM mobile flow).
 * From the menu, users pick:
 *   - "Daily Posting"  → /posting (handled inside ChoiceScreen)
 *   - "Summary"        → MobileCommandView (command center)
 *   - "Staff"          → OfficerTaskView (individual shift view)
 *
 * Each child view's back button returns to the menu.
 * Re-hydrates mobile store after each sync pull.
 */
import { useState, useEffect, useRef } from 'react'
import { useMobileStore } from '../stores/mobile'
import { useSyncStore } from '../stores/sync'
import ChoiceScreen from '../components/mobile/ChoiceScreen'
import OfficerTaskView from '../components/mobile/OfficerTaskView'
import MobileCommandView from '../components/mobile/MobileCommandView'

type View = 'menu' | 'officer' | 'command'

export default function MobileLayout() {
  const hydrateFromStorage = useMobileStore(s => s.hydrateFromStorage)
  const pullCount = useSyncStore(s => s.pullCount)

  const [view, setView] = useState<View>(() => {
    try {
      const stored = sessionStorage.getItem('fom_mobile_appView')
      if (stored === 'officer' || stored === 'command' || stored === 'menu') return stored
    } catch { /* silent */ }
    return 'menu'
  })

  // Keep a ref so the back button handler always sees the current view
  const viewRef = useRef(view)
  useEffect(() => { viewRef.current = view }, [view])

  useEffect(() => {
    try { sessionStorage.setItem('fom_mobile_appView', view) } catch { /* silent */ }
  }, [view])

  // Intercept Android hardware back button via Capacitor
  useEffect(() => {
    let cleanup: (() => void) | null = null
    import('@capacitor/app').then(({ App: CapApp }) => {
      const handler = CapApp.addListener('backButton', () => {
        if (viewRef.current !== 'menu') {
          // Any sub-view → go back to menu
          setView('menu')
        } else {
          // Already at menu → exit app
          CapApp.exitApp()
        }
      })
      cleanup = () => { handler.then(h => h.remove()) }
    }).catch(() => { /* not running in Capacitor (web) — ignore */ })
    return () => { cleanup?.() }
  }, [])

  // Re-hydrate after each successful sync pull
  useEffect(() => {
    hydrateFromStorage()
  }, [pullCount])

  const goMenu = () => setView('menu')

  if (view === 'command') {
    return <MobileCommandView onBack={goMenu} />
  }
  if (view === 'officer') {
    return <OfficerTaskView onBack={goMenu} />
  }
  return <ChoiceScreen onChoice={(v) => setView(v)} />
}
